// Rebuy widget variant classes (UBB-118)
// Two responsibilities:
//
// 1. WIDGET_VARIANTS map — PDP widgets (294387, 298774) have data-rebuy-variant set by JS
//    because both share the same Vue template and the variant is resolved at runtime.
//    Widget 300985 (cart page) is also listed so it falls through to the bindAccent path
//    (its data-rebuy-variant="b" is already hardcoded in the template, the attribute-set
//    step is a no-op).
//
// 2. Generic scan — any .rebuy-widget[data-rebuy-variant] NOT in WIDGET_VARIANTS (e.g.
//    upsell widgets 301228/301264/301265 and any future ones) gets bindAccent() called
//    automatically. No WIDGET_VARIANTS entry needed for widgets that hardcode the attribute.
//
// Uses MutationObserver to handle Rebuy's async render.
// Guard: window.__rebuyVariantsInitDone prevents double-init on product pages, which
// load both global.autojs.js and product.autojs.js (both call this function).

import { bindAccent } from '../global/section-header-typing.js';

const WIDGET_VARIANTS = {
  'rebuy-widget-294387': 'a',
  'rebuy-widget-298774': 'b',
  'rebuy-widget-300985': 'b', // cart page — variant hardcoded in template; listed for bindAccent
};

function bindAccentOnWidget(el) {
  // Bind the title animation. Rebuy's v-if="config.language.title != ''" means the title
  // element is absent until the widget config loads — that second DOM mutation arrives AFTER
  // data-rebuy-variant is already set, so we must re-check on every MutationObserver tick.
  // bindAccent() is idempotent (data-sh-accent-typing-bound flag), so extra calls are safe.
  // requestAnimationFrame lets Vue's synchronous mount cycle finish before we query.
  //
  // We query .primary-title WITHOUT the [data-animate-accent] constraint: if Rebuy falls
  // back to its default Vue template (e.g. the custom <script id="rebuy-widget-NNN"> wasn't
  // found in time), the rendered element is <h4 class="primary-title"> with no attribute.
  // We set the attribute here so bindAccent()'s opt-in gate passes regardless of template.
  requestAnimationFrame(() => {
    const primaryTitle = el.querySelector('.primary-title');
    if (primaryTitle) {
      primaryTitle.setAttribute('data-animate-accent', 'true');
      bindAccent(primaryTitle);
    }
  });
}

function applyVariants() {
  // Pass 1 — explicit map: set data-rebuy-variant on PDP widgets + bindAccent all listed widgets.
  Object.entries(WIDGET_VARIANTS).forEach(([id, variant]) => {
    const el = document.getElementById(id);
    if (!el) return;

    // Assign data-rebuy-variant once (CSS depends on it from the first render).
    if (!el.hasAttribute('data-rebuy-variant')) {
      el.setAttribute('data-rebuy-variant', variant);
    }

    bindAccentOnWidget(el);
  });

  // Pass 2 — generic scan: bindAccent on any rendered .rebuy-widget that already has
  // data-rebuy-variant hardcoded (upsell widgets and any future ones added without a
  // WIDGET_VARIANTS entry). Skip widgets already handled in pass 1.
  document
    .querySelectorAll('.rebuy-widget[data-rebuy-variant]')
    .forEach((el) => {
      if (WIDGET_VARIANTS[el.id]) return;
      bindAccentOnWidget(el);
    });
}

export default function initRebuyVariants() {
  // Guard: global.autojs.js and product.autojs.js both call this on product pages.
  // The first call (global) wins; the second is a no-op.
  if (window.__rebuyVariantsInitDone) return;
  window.__rebuyVariantsInitDone = true;

  applyVariants(); // handle widgets already in the DOM at DOMContentLoaded
  const observer = new MutationObserver(applyVariants);
  observer.observe(document.body, { childList: true, subtree: true });
}
